import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Clock3,
  Database,
  Search,
  Server as ServerGlyph,
  ShieldAlert,
  SlidersHorizontal,
  Timer,
  Wifi,
  X,
} from "lucide-react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import {
  deleteComputer,
  getComputerHistory,
  getComputers,
  updateComputerName,
} from "../services/api";
import { isAboveWarning, metricSeverity } from "../utils/metricSeverity";
import logo from "../logo/logo.png";
import {
  ActivationToggle,
  ActionSlot,
  AlertBar,
  CardModalBackdrop,
  CardModalClose,
  CardModalDialog,
  ChartsContainer,
  Container,
  Content,
  EmptyState,
  ExpandedServerCard,
  DashboardArea,
  FilterButton,
  FilterGrid,
  FilterLabel,
  GaugeArc,
  GaugeBody,
  GaugeSvg,
  GaugeTrack,
  Header,
  HeaderSummary,
  EnvironmentBadge,
  SummaryChip,
  IdentityHeader,
  IdentityPanel,
  LogoTitle,
  Main,
  MetricCard,
  MetricCardHeader,
  MetricNumber,
  MetricsGrid,
  MetricTitle,
  MonitorPanel,
  PresenceDot,
  ServerCard,
  ServerCardViewport,
  ServerIcon,
  ServerList,
  ServerListItem,
  ServerNameButton,
  Sidebar,
  SidebarCollapse,
  SidebarCount,
  SidebarHeader,
  SidebarServerButton,
  SidebarServerIdentity,
  SidebarServerState,
  SidebarTitle,
  SearchField,
  StatusBadge,
  StatusDot,
  StatusItem,
  StatusStrip,
  StatusText,
} from "../styles/DashboardStyles";
import DropdownMenu from "./DropdownMenu";
import HistorySidebar from "./HistorySidebar";
import UsageChart from "./UsageChart";
import CloudMonitoring, { CloudSidebar } from "./CloudMonitoring";
import MonitoringNavigation from "./MonitoringNavigation";
import useMonitoringTabs from "../hooks/useMonitoringTabs";
import useCloudMonitoring, { cloudOnline, cloudTone } from "../hooks/useCloudMonitoring";

const COLORS = {
  cpu: "#35d46f",
  cpuChart: "#f3df16",
  memory: "#3698ff",
  storage: "#9b54e8",
  warning: "#f5ad18",
  danger: "#ff425b",
  muted: "#8d99a5",
  usageNormal: "#33cc33",
  usageWarning: "#ffab07",
  usageDanger: "#e92904",
};

const CARD_WIDTH = 300;
const CARD_HEIGHT = 150;
const GRID_GAP = 8;
const MAX_CARD_SCALE = 1.2;
const DESKTOP_COLUMN_COUNT = 4;
const DESKTOP_FOUR_COLUMN_MIN_VIEWPORT = 1120;
const DESKTOP_FOUR_COLUMN_MIN_CONTENT = 850;
const INACTIVE_SERVERS_STORAGE_KEY = "inactiveServers";

const readStoredIdSet = (key) => {
  if (typeof window === "undefined") return new Set();

  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || "[]");
    return new Set(Array.isArray(stored) ? stored.map(String) : []);
  } catch (error) {
    console.warn(`Não foi possível ler ${key} do navegador:`, error);
    return new Set();
  }
};

const writeStoredIdSet = (key, ids) => {
  try {
    window.localStorage.setItem(key, JSON.stringify([...ids]));
  } catch (error) {
    console.warn(`Não foi possível salvar ${key} no navegador:`, error);
  }
};

const calculateDashboardLayout = (
  cardCount,
  width,
  height,
  preferredColumns = null
) => {
  if (!cardCount || width <= 0 || height <= 0) {
    return {
      columns: 1,
      rows: 1,
      height: Math.max(280, height),
      scale: 1,
      zoomBoost: 1,
      cellWidth: CARD_WIDTH,
      cellHeight: CARD_HEIGHT,
    };
  }

  let bestLayout = null;
  const maxColumns = Math.min(cardCount, 12);
  const normalizedPreferredColumns = Number.isInteger(preferredColumns)
    ? Math.max(1, preferredColumns)
    : null;
  const columnCandidates = normalizedPreferredColumns
    ? [normalizedPreferredColumns]
    : Array.from({ length: maxColumns }, (_, index) => index + 1);

  for (const columns of columnCandidates) {
    const rows = Math.ceil(cardCount / columns);
    const cellWidth = (width - GRID_GAP * (columns - 1)) / columns;
    const cellHeight = Math.min(
      (height - GRID_GAP * (rows - 1)) / rows,
      cellWidth * (CARD_HEIGHT / CARD_WIDTH)
    );

    if (cellWidth <= 0 || cellHeight <= 0) continue;

    const scale = Math.max(
      0.01,
      Math.min(MAX_CARD_SCALE, cellWidth / CARD_WIDTH, cellHeight / CARD_HEIGHT)
    );
    const unusedCells = columns * rows - cardCount;
    const cellRatio = cellWidth / cellHeight;
    const portraitPenalty = Math.max(0, 1.9 - cellRatio) * 0.025;
    const score = scale - unusedCells * 0.003 - portraitPenalty;

    if (!bestLayout || score > bestLayout.score) {
      bestLayout = { columns, rows, scale, score, cellWidth, cellHeight };
    }
  }

  return {
    columns: bestLayout?.columns || 1,
    rows: bestLayout?.rows || cardCount,
    height,
    scale: bestLayout?.scale || 1,
    zoomBoost: 1,
    cellWidth: bestLayout?.cellWidth || CARD_WIDTH,
    cellHeight: bestLayout?.cellHeight || CARD_HEIGHT,
  };
};

const safePercent = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.min(100, Math.max(0, numericValue));
};

const formatPercent = (value) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safePercent(value));

const getMinutesSinceUpdate = (dateString) => {
  if (!dateString) return Number.POSITIVE_INFINITY;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return Number.POSITIVE_INFINITY;

  return Math.max(0, (Date.now() - date.getTime()) / 60000);
};

const isComputerOnline = (computer) =>
  getMinutesSinceUpdate(computer.last_updated) <= 3;

const isComputerActive = (computer) => computer.is_active !== false;

const formatUpdateTime = (dateString) => {
  if (!dateString) return "Sem registro";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Sem registro";

  return date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getCardTone = (computer, isProblem, isOnline) => {
  if (isProblem || !isOnline) return "danger";

  const hasWarning = [computer.cpu_usage, computer.memory_usage, computer.disk_usage].some(isAboveWarning);
  return hasWarning ? "warning" : "healthy";
};

const getMetricColor = (type, value) => {
  const severity = metricSeverity(safePercent(value));
  if (severity === "critical") return type === "cpu" ? COLORS.danger : COLORS.usageDanger;
  if (severity === "warning") return COLORS.usageWarning;
  return type === "cpu" ? COLORS.cpu : COLORS.usageNormal;
};

const GAUGE_RING_PATH = "M 22.42 77.58 A 39 39 0 1 1 77.58 77.58";

const MetricSummary = ({ label, type, value }) => {
  const gaugeId = useId().replace(/:/g, "");
  const percentage = safePercent(value);
  const color = getMetricColor(type, percentage);
  const displayValue = `${formatPercent(percentage)}%`;

  return (
    <MetricCard
      role="meter"
      aria-label={`${label}: ${displayValue}`}
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={Number(percentage.toFixed(1))}
    >
      <MetricCardHeader $color={color}>
        <MetricTitle>{label}</MetricTitle>
      </MetricCardHeader>
      <GaugeBody>
        <GaugeSvg
          viewBox="0 0 100 92"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id={`${gaugeId}-progress`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.72" />
              <stop offset="55%" stopColor={color} />
              <stop offset="100%" stopColor={color} stopOpacity="0.88" />
            </linearGradient>
          </defs>
          <GaugeTrack d={GAUGE_RING_PATH} pathLength="100" />
          <GaugeArc
            d={GAUGE_RING_PATH}
            pathLength="100"
            $glowColor={color}
            stroke={`url(#${gaugeId}-progress)`}
            strokeDasharray={`${percentage} 100`}
          />
          <MetricNumber x="50" y="59" textAnchor="middle">
            {formatPercent(percentage)}<tspan fontSize="12">%</tspan>
          </MetricNumber>
        </GaugeSvg>
      </GaugeBody>
    </MetricCard>
  );
};

const ServerCardContent = ({
  computer,
  computerHistory,
  displayName,
  isOnline,
  isProblem,
  tone,
  expanded = false,
  onOpenHistory,
  onEdit,
  onDelete,
}) => (
  <>
    <IdentityPanel>
      <IdentityHeader>
        <ServerIcon>
          <ServerGlyph aria-hidden="true" />
          <PresenceDot $online={isOnline} />
        </ServerIcon>

        <div>
          <ServerNameButton
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenHistory();
            }}
            title="Abrir histórico do servidor"
          >
            {displayName}
          </ServerNameButton>
          <StatusBadge $tone={tone}>
            {!isOnline
              ? "Offline"
              : isProblem
                ? "Crítico"
                : tone === "warning"
                  ? "Alerta"
                  : "Online"}
          </StatusBadge>
        </div>

        <ActionSlot onClick={(event) => event.stopPropagation()}>
          <DropdownMenu compact onEdit={onEdit} onDelete={onDelete} />
        </ActionSlot>
      </IdentityHeader>
    </IdentityPanel>

    <MonitorPanel>
      <MetricsGrid>
        <MetricSummary label="CPU" type="cpu" value={computer.cpu_usage} />
        <MetricSummary
          label="Memória"
          type="memory"
          value={computer.memory_usage}
        />
        <MetricSummary
          label="Disco"
          type="storage"
          value={computer.disk_usage}
        />
      </MetricsGrid>

      <ChartsContainer>
        <UsageChart
          data={computerHistory}
          dataKey="cpu_usage"
          title="CPU (1h)"
          color={getMetricColor("cpu", computer.cpu_usage)}
          expanded={expanded}
        />
        <UsageChart
          data={computerHistory}
          dataKey="memory_usage"
          title="Memória (1h)"
          color={isAboveWarning(computer.memory_usage) ? getMetricColor("memory", computer.memory_usage) : COLORS.memory}
          expanded={expanded}
        />
      </ChartsContainer>

      <StatusStrip>
        <StatusItem $color={COLORS.muted}>
          <Timer size={16} aria-hidden="true" />
          <StatusText>
            <span>Última leitura</span>
            <strong>{formatUpdateTime(computer.last_updated)}</strong>
          </StatusText>
        </StatusItem>
        <StatusItem $color={COLORS.memory}>
          <Database size={16} aria-hidden="true" />
          <StatusText $color={COLORS.memory}>
            <span>Amostras</span>
            <strong>{computerHistory.length}</strong>
          </StatusText>
        </StatusItem>
        <StatusItem
          $color={computerHistory.length ? COLORS.cpu : COLORS.warning}
        >
          <CircleCheck size={16} aria-hidden="true" />
          <StatusText
            $color={computerHistory.length ? COLORS.cpu : COLORS.warning}
          >
            <span>Histórico</span>
            <strong>
              {computerHistory.length ? "Registrando" : "Aguardando"}
            </strong>
          </StatusText>
        </StatusItem>
        <StatusItem $color={isOnline ? COLORS.cpu : COLORS.danger}>
          <Wifi size={16} aria-hidden="true" />
          <StatusText $color={isOnline ? COLORS.cpu : COLORS.danger}>
            <span>Comunicação</span>
            <strong>{isOnline ? "Online" : "Sem sinal"}</strong>
          </StatusText>
        </StatusItem>
      </StatusStrip>
    </MonitorPanel>
  </>
);

const Dashboard = () => {
  const [computers, setComputers] = useState([]);
  const [history, setHistory] = useState({});
  const [selectedComputer, setSelectedComputer] = useState(null);
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [apiError, setApiError] = useState("");
  const [expandedComputerId, setExpandedComputerId] = useState(null);
  const [cloudHours, setCloudHours] = useState(1);
  const [cloudSearch, setCloudSearch] = useState("");
  const [selectedCloud, setSelectedCloud] = useState(null);
  const tabs = useMonitoringTabs(Boolean(expandedComputerId) || showHistorySidebar);
  const cloud = useCloudMonitoring(cloudHours);
  const isCloud = tabs.tab === "cloud";
  const mainRef = useRef(null);
  const [dashboardLayout, setDashboardLayout] = useState({
    columns: 1,
    rows: 1,
    height: 560,
    scale: 1,
    zoomBoost: 1,
    cardDesignWidth: CARD_WIDTH,
    cardDesignHeight: CARD_HEIGHT,
    cardRenderedWidth: CARD_WIDTH,
    cardRenderedHeight: CARD_HEIGHT,
  });

  const activeComputers = useMemo(
    () => computers.filter(isComputerActive),
    [computers]
  );

  const expandedComputer = useMemo(
    () =>
      computers.find(
        (computer) => String(computer.id) === String(expandedComputerId)
      ) || null,
    [computers, expandedComputerId]
  );

  useEffect(() => {
    if (!expandedComputer) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setExpandedComputerId(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [expandedComputer]);

  const { alertMessage, problematic, attentionCount } = useMemo(() => {
    const offline = [], highCpu = [], highMemory = [], highStorage = [];
    const problemIds = new Set(), attentionIds = new Set();
    activeComputers.forEach(computer => {
      if (!computer.last_updated) return;
      const displayName = computer.custom_name || computer.hostname;
      if (!isComputerOnline(computer)) {
        offline.push(displayName);
        problemIds.add(computer.id);
        attentionIds.add(computer.id);
      }
      for (const [value, names] of [[computer.cpu_usage, highCpu],
        [computer.memory_usage, highMemory], [computer.disk_usage, highStorage]]) {
        if (isAboveWarning(value)) { names.push(displayName); attentionIds.add(computer.id); }
        if (metricSeverity(value) === "critical") problemIds.add(computer.id);
      }
    });
    const alertParts = [];
    if (offline.length) alertParts.push(`Offline: ${offline.join(", ")}`);
    if (highCpu.length) alertParts.push(`CPU alta: ${highCpu.join(", ")}`);
    if (highMemory.length) alertParts.push(`Memória alta: ${highMemory.join(", ")}`);
    if (highStorage.length) alertParts.push(`Storage alto: ${highStorage.join(", ")}`);
    return { alertMessage: alertParts.join(" | "), problematic: [...problemIds], attentionCount: attentionIds.size };
  }, [activeComputers]);

  const { filteredComputers, visibleComputers, severityCounts } = useMemo(() => {
    const counts = {
      all: computers.length,
      critical: 0,
      warning: 0,
      normal: 0,
      offline: 0,
      inactive: 0,
    };
    const query = search.trim().toLocaleLowerCase("pt-BR");
    const problemIds = new Set(problematic);

    const matches = computers.filter((computer) => {
      const active = isComputerActive(computer);
      const online = isComputerOnline(computer);
      const critical = active && problemIds.has(computer.id);
      const tone = getCardTone(computer, critical, online);

      if (!active) {
        counts.inactive += 1;
      } else {
        if (!online) counts.offline += 1;
        if (critical) counts.critical += 1;
        else if (tone === "warning") counts.warning += 1;
        else counts.normal += 1;
      }

      const matchesSearch = [computer.hostname, computer.custom_name].some((name) =>
        String(name || "").toLocaleLowerCase("pt-BR").includes(query)
      );

      const matchesSeverity = {
        all: true,
        critical,
        warning: active && !critical && tone === "warning",
        normal: active && !critical && tone === "healthy",
        offline: active && !online,
        inactive: !active,
      }[severity];

      return matchesSearch && Boolean(matchesSeverity);
    });

    return {
      filteredComputers: matches,
      visibleComputers: matches.filter(isComputerActive),
      severityCounts: counts,
    };
  }, [computers, problematic, search, severity]);

  useLayoutEffect(() => {
    const mainElement = mainRef.current;
    if (!mainElement) return undefined;

    let animationFrame;

    const updateLayout = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const bounds = mainElement.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const availableHeight = Math.max(
          1,
          viewportHeight - bounds.top - GRID_GAP
        );
        const useFourColumnDesktopLayout =
          window.innerWidth >= DESKTOP_FOUR_COLUMN_MIN_VIEWPORT &&
          mainElement.clientWidth >= DESKTOP_FOUR_COLUMN_MIN_CONTENT;
        const isSmallScreen = window.innerWidth <= 860;
        const preferredColumns = isSmallScreen ? (mainElement.clientWidth >= 600 ? 2 : 1)
          : useFourColumnDesktopLayout ? DESKTOP_COLUMN_COUNT
          : mainElement.clientWidth >= 700 ? 3
          : mainElement.clientWidth >= 480 ? 2 : 1;
        const fittedLayout = calculateDashboardLayout(
          visibleComputers.length,
          mainElement.clientWidth,
          availableHeight,
          preferredColumns
        );
        const cardDesignWidth =
          fittedLayout.cellWidth / Math.max(0.01, fittedLayout.scale);
        const cardDesignHeight =
          fittedLayout.cellHeight / Math.max(0.01, fittedLayout.scale);
        const scale = fittedLayout.scale;
        const nextLayout = {
          ...fittedLayout,
          scale,
          cardDesignWidth,
          cardDesignHeight,
          cardRenderedWidth: cardDesignWidth * scale,
          cardRenderedHeight: cardDesignHeight * scale,
        };

        setDashboardLayout((currentLayout) => {
          const unchanged =
            currentLayout.columns === nextLayout.columns &&
            currentLayout.rows === nextLayout.rows &&
            Math.abs(currentLayout.height - nextLayout.height) < 1 &&
            Math.abs(currentLayout.scale - nextLayout.scale) < 0.001 &&
            Math.abs(currentLayout.zoomBoost - nextLayout.zoomBoost) < 0.001 &&
            Math.abs(currentLayout.cardDesignWidth - nextLayout.cardDesignWidth) < 1 &&
            Math.abs(currentLayout.cardDesignHeight - nextLayout.cardDesignHeight) < 1;

          return unchanged ? currentLayout : nextLayout;
        });
      });
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    window.visualViewport?.addEventListener("resize", updateLayout);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateLayout);
    resizeObserver?.observe(mainElement);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateLayout);
      window.visualViewport?.removeEventListener("resize", updateLayout);
      resizeObserver?.disconnect();
    };
  }, [visibleComputers.length, alertMessage, sidebarCollapsed, isCloud]);

  useEffect(() => {
    let inProgress = false;
    let historyBootstrapped = false;
    const fetchData = async () => {
      if (inProgress) return;
      inProgress = true;
      try {
        const data = await getComputers();

        const excludedIds = readStoredIdSet("excludedServers");
        const inactiveIds = readStoredIdSet(INACTIVE_SERVERS_STORAGE_KEY);

        const updated = data
          .filter(
            (computer) =>
              computer &&
              computer.id &&
              !excludedIds.has(String(computer.id))
          )
          .map((computer) => ({
            ...computer,
            displayName: computer.custom_name || computer.hostname,
            is_active: !inactiveIds.has(String(computer.id)),
          }));

        setComputers(updated);
        setLastRefresh(new Date());
        setApiError("");

        if (!historyBootstrapped) {
          const historicalData = {};
          for (const computer of updated) {
            try {
              const computerHistory = await getComputerHistory(computer.id, 1);
              historicalData[computer.id] = computerHistory.map((item) => ({
                time: new Date(item.recorded_at).toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                cpu_usage: Number(item.cpu_usage),
                memory_usage: Number(item.memory_usage),
                disk_usage: Number(item.disk_usage),
                last_updated: item.recorded_at,
                timestamp: new Date(item.recorded_at),
              }));
            } catch (error) {
              console.error(`Erro ao buscar histórico de ${computer.hostname}:`, error);
              historicalData[computer.id] = [];
            }
          }
          setHistory(historicalData);
          historyBootstrapped = true;
        } else {
          // A lista mantém os números e os gráficos atualizados a cada 5s.
          // O histórico completo é carregado só ao abrir o painel, evitando
          // dezenas de consultas repetidas por intervalo.
          setHistory((previous) => {
            const next = {};
            const cutoff = Date.now() - 60 * 60 * 1000;
            for (const computer of updated) {
              const points = (previous[computer.id] || []).filter(
                (point) => new Date(point.timestamp).getTime() >= cutoff
              );
              const timestamp = new Date(computer.last_updated);
              const latest = points.length
                ? new Date(points[points.length - 1].timestamp).getTime()
                : 0;
              if (Number.isFinite(timestamp.getTime()) && timestamp.getTime() > latest) {
                points.push({
                  time: timestamp.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
                  cpu_usage: Number(computer.cpu_usage),
                  memory_usage: Number(computer.memory_usage),
                  disk_usage: Number(computer.disk_usage),
                  last_updated: computer.last_updated,
                  timestamp,
                });
              }
              next[computer.id] = points;
            }
            return next;
          });
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setApiError("A API não respondeu. Os dados abaixo podem estar desatualizados.");
      } finally {
        inProgress = false;
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleEdit = async (id) => {
    Swal.fire({
      title: "Editar nome do servidor",
      input: "text",
      inputLabel: "Digite o novo nome:",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Salvar",
      inputValidator: (value) => {
        if (!value) return "O nome não pode estar vazio!";
        return undefined;
      },
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        const newName = result.value;

        try {
          await updateComputerName(id, newName);
          setComputers((previous) =>
            previous.map((computer) =>
              computer.id === id
                ? { ...computer, custom_name: newName, displayName: newName }
                : computer
            )
          );

          const edited = JSON.parse(
            localStorage.getItem("customNames") || "{}"
          );
          delete edited[id];
          localStorage.setItem("customNames", JSON.stringify(edited));

          Swal.fire("Sucesso!", "Nome atualizado com sucesso.", "success");
        } catch (error) {
          console.error("Erro ao atualizar:", error);
          Swal.fire("Erro!", "Não foi possível atualizar o nome.", "error");
        }
      }
    });
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Tem certeza?",
      text: "Você está prestes a remover este servidor do banco de dados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteComputer(id);
          setComputers((previous) =>
            previous.filter((computer) => computer.id !== id)
          );

          const edited = JSON.parse(
            localStorage.getItem("customNames") || "{}"
          );
          delete edited[id];
          localStorage.setItem("customNames", JSON.stringify(edited));

          const storedId = String(id);
          const excluded = readStoredIdSet("excludedServers");
          excluded.delete(storedId);
          writeStoredIdSet("excludedServers", excluded);

          const inactive = readStoredIdSet(INACTIVE_SERVERS_STORAGE_KEY);
          inactive.delete(storedId);
          writeStoredIdSet(INACTIVE_SERVERS_STORAGE_KEY, inactive);

          Swal.fire(
            "Removido!",
            "O servidor foi removido com sucesso.",
            "success"
          );
        } catch (error) {
          console.error("Erro ao deletar:", error);
          Swal.fire("Erro!", "Não foi possível remover o servidor.", "error");
        }
      }
    });
  };

  const handleToggleActive = (computer) => {
    const nextActiveState = !isComputerActive(computer);
    const storedId = String(computer.id);
    const inactiveIds = readStoredIdSet(INACTIVE_SERVERS_STORAGE_KEY);

    if (nextActiveState) {
      inactiveIds.delete(storedId);
    } else {
      inactiveIds.add(storedId);
    }
    writeStoredIdSet(INACTIVE_SERVERS_STORAGE_KEY, inactiveIds);

    setComputers((previous) =>
      previous.map((item) =>
        item.id === computer.id
          ? { ...item, is_active: nextActiveState }
          : item
      )
    );

    if (
      !nextActiveState &&
      selectedComputer &&
      String(selectedComputer.id) === storedId
    ) {
      handleCloseHistory();
    }
  };

  const handleOpenHistory = (computer) => {
    setSelectedComputer(computer);
    setShowHistorySidebar(true);
  };

  const handleCloseHistory = () => {
    setShowHistorySidebar(false);
    setTimeout(() => setSelectedComputer(null), 300);
  };

  const onlineCount = activeComputers.filter(isComputerOnline).length;
  const expandedComputerHistory = expandedComputer
    ? history[expandedComputer.id] || []
    : [];
  const expandedComputerOnline = expandedComputer
    ? isComputerOnline(expandedComputer)
    : false;
  const expandedComputerProblem = expandedComputer
    ? problematic.includes(expandedComputer.id)
    : false;
  const expandedComputerTone = expandedComputer
    ? getCardTone(
        expandedComputer,
        expandedComputerProblem,
        expandedComputerOnline
      )
    : "healthy";
  const expandedComputerName = expandedComputer
    ? expandedComputer.custom_name || expandedComputer.hostname
    : "";
  const filters = [
    { key: "all", label: "Todos", color: COLORS.memory },
    { key: "critical", label: "Crítico", color: COLORS.danger },
    { key: "warning", label: "Alerta", color: COLORS.warning },
    { key: "normal", label: "Normal", color: COLORS.cpu },
    { key: "offline", label: "Offline", color: COLORS.muted },
    { key: "inactive", label: "Desativado", color: COLORS.muted },
  ];

  return (
    <Container $cloud={isCloud}>
      <Header>
        <LogoTitle>
          <img src={logo} alt="Vyoo" />
          <span>Monitoramento Vyoo</span>
          <EnvironmentBadge>{process.env.REACT_APP_PREVIEW_MODE === "true" ? "Prévia local" : "Produção"}</EnvironmentBadge>
        </LogoTitle>
        <HeaderSummary>
          <SummaryChip $color={COLORS.cpu}>
            <ServerGlyph aria-hidden="true" /><strong>{isCloud ? cloud.servers.length : computers.length}</strong> servidores
          </SummaryChip>
          <SummaryChip $color={COLORS.cpu}>
            <StatusDot aria-hidden="true" /><strong>{isCloud ? cloud.servers.filter(cloudOnline).length : onlineCount}</strong> online
          </SummaryChip>
          <SummaryChip $color={(isCloud ? cloud.servers.some(s => cloudTone(s) !== "normal") : attentionCount) ? COLORS.warning : COLORS.cpu}>
            <ShieldAlert aria-hidden="true" /><strong>{isCloud ? cloud.servers.filter(s => cloudTone(s) !== "normal").length : attentionCount}</strong> alertas
          </SummaryChip>
          <SummaryChip $color={COLORS.memory} title={lastRefresh ? `Última consulta: ${lastRefresh.toLocaleTimeString("pt-BR")}` : "Aguardando a primeira consulta"}>
            <Clock3 aria-hidden="true" />
            {(isCloud ? cloud.error : apiError) ? "Sem conexão" : (isCloud ? cloud.updatedAt : lastRefresh) ? "Atualizado" : "Conectando"}<span>• a cada {isCloud ? 3 : 5}s</span>
          </SummaryChip>
        </HeaderSummary>
      </Header>

      <Content $collapsed={sidebarCollapsed} $cloud={isCloud}>
        <Sidebar
          aria-label="Servidores monitorados"
          $collapsed={sidebarCollapsed}
          $cloud={isCloud}
        >
          <MonitoringNavigation {...tabs} collapsed={sidebarCollapsed} serverCount={computers.length} cloudCount={cloud.servers.length} />
          {!isCloud && <>
          <SidebarHeader>
            <SidebarTitle>
              <ServerGlyph size={17} aria-hidden="true" />
              {!sidebarCollapsed && "Servidores monitorados"}
            </SidebarTitle>
            {!sidebarCollapsed && <SidebarCount aria-label={`${computers.length} servidores`}>
              {computers.length}
            </SidebarCount>}
          </SidebarHeader>

          {!sidebarCollapsed && <>
          <SearchField>
            <Search size={14} aria-hidden="true" />
            <input aria-label="Buscar servidor" placeholder="Buscar servidor..." value={search} onChange={(event) => setSearch(event.target.value)} />
          </SearchField>
          <FilterLabel><SlidersHorizontal size={12} aria-hidden="true" /> Severidade</FilterLabel>
          <FilterGrid aria-label="Filtrar por severidade">
            {filters.map((filter) => (
              <FilterButton key={filter.key} type="button" $color={filter.color} $selected={severity === filter.key}
                aria-pressed={severity === filter.key} onClick={() => setSeverity(filter.key)}>
                <span>{filter.label}</span><strong>{severityCounts[filter.key]}</strong>
              </FilterButton>
            ))}
          </FilterGrid>
          <ServerList $items={filteredComputers.length}>
            {filteredComputers.map((computer, index) => {
              const isActive = isComputerActive(computer);
              const isOnline = isComputerOnline(computer);
              const isProblem =
                isActive && problematic.includes(computer.id);
              const displayName = computer.custom_name || computer.hostname;

              return (
                <ServerListItem
                  key={computer.id}
                  $active={isActive}
                  $problematic={isProblem}
                >
                  <SidebarServerButton
                    type="button"
                    $active={isActive}
                    $online={isOnline}
                    onClick={() => handleOpenHistory(computer)}
                    title="Abrir histórico do servidor"
                  >
                    <StatusDot
                      $color={
                        !isActive
                          ? COLORS.muted
                          : isOnline
                            ? COLORS.cpu
                            : COLORS.danger
                      }
                      aria-hidden="true"
                    />
                    <SidebarServerIdentity>
                      <strong>{displayName}</strong>
                      <SidebarServerState
                        $active={isActive}
                        $online={isOnline}
                      >
                        {isActive
                          ? isOnline
                            ? "Online"
                            : "Offline"
                          : "Desativado"}
                      </SidebarServerState>
                    </SidebarServerIdentity>
                  </SidebarServerButton>
                  <ActivationToggle
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    aria-label={`${
                      isActive ? "Desativar" : "Ativar"
                    } ${displayName}`}
                    title={isActive ? "Desativar servidor" : "Ativar servidor"}
                    $active={isActive}
                    onClick={() => handleToggleActive(computer)}
                  >
                    <span aria-hidden="true" />
                  </ActivationToggle>
                  <DropdownMenu
                    compact
                    dropUp={index >= filteredComputers.length - 3}
                    onEdit={() => handleEdit(computer.id)}
                    onDelete={() => handleDelete(computer.id)}
                  />
                </ServerListItem>
              );
            })}
          </ServerList>
          </>}
          </>}
          {isCloud && !sidebarCollapsed && <CloudSidebar servers={cloud.servers} search={cloudSearch} setSearch={setCloudSearch}
            selected={selectedCloud} setSelected={setSelectedCloud} />}
          <SidebarCollapse type="button" onClick={() => setSidebarCollapsed((value) => !value)}
            aria-label={sidebarCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"} aria-expanded={!sidebarCollapsed}>
            {sidebarCollapsed ? <ChevronRight size={15} /> : <><ChevronLeft size={15} /> Recolher</>}
          </SidebarCollapse>
        </Sidebar>

        {isCloud ? <CloudMonitoring data={cloud} hours={cloudHours} setHours={setCloudHours}
          search={cloudSearch} selected={selectedCloud} setSelected={setSelectedCloud} /> : <DashboardArea>
        {apiError && (
          <AlertBar role="alert">
            <ShieldAlert size={16} aria-hidden="true" />
            <strong>Conexão indisponível</strong>
            <span>{apiError}</span>
          </AlertBar>
        )}
        {alertMessage && (
          <AlertBar role="alert">
            <ShieldAlert size={16} aria-hidden="true" />
            <strong>
              {attentionCount} {attentionCount === 1
                ? "servidor requer atenção"
                : "servidores requerem atenção"}
            </strong>
            <span>{alertMessage.replace("Problemas detectados — ", "")}</span>
          </AlertBar>
        )}
        <Main
          ref={mainRef}
          $columns={dashboardLayout.columns}
          $rows={dashboardLayout.rows}
          $height={dashboardLayout.height}
          $cardWidth={dashboardLayout.cardRenderedWidth}
          $cardHeight={dashboardLayout.cardRenderedHeight}
        >
          {visibleComputers.length === 0 ? (
            <EmptyState>
              <div>
                <ServerGlyph size={44} aria-hidden="true" />
                <strong>
                  {computers.length === 0
                    ? "Nenhum computador monitorado"
                    : activeComputers.length === 0 ? "Nenhum servidor ativo" : "Nenhum servidor ativo neste filtro"}
                </strong>
                <span>
                  {computers.length === 0
                    ? "Os servidores aparecerão aqui assim que enviarem dados."
                    : severity === "inactive" ? "Os servidores desativados ficam na lista lateral. Ative um deles para exibir seu card."
                    : "Ajuste a busca ou ative um servidor na lista lateral para exibir seu card."}
                </span>
              </div>
            </EmptyState>
          ) : (
            visibleComputers.map((computer) => {
              const isOnline = isComputerOnline(computer);
              const isProblem = problematic.includes(computer.id);
              const tone = getCardTone(computer, isProblem, isOnline);
              const computerHistory = history[computer.id] || [];
              const displayName = computer.custom_name || computer.hostname;

              return (
                <ServerCardViewport key={computer.id}>
                  <ServerCard
                    aria-label={`Servidor ${displayName}`}
                    role="button"
                    tabIndex={0}
                    title="Clique para ampliar o card"
                    $tone={tone}
                    $problematic={isProblem}
                    $scale={dashboardLayout.scale}
                    $width={dashboardLayout.cardDesignWidth}
                    $height={dashboardLayout.cardDesignHeight}
                    onClick={() => setExpandedComputerId(computer.id)}
                    onKeyDown={(event) => {
                      if (
                        event.target === event.currentTarget &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        setExpandedComputerId(computer.id);
                      }
                    }}
                  >
                    <ServerCardContent
                      computer={computer}
                      computerHistory={computerHistory}
                      displayName={displayName}
                      isOnline={isOnline}
                      isProblem={isProblem}
                      tone={tone}
                      onOpenHistory={() => handleOpenHistory(computer)}
                      onEdit={() => handleEdit(computer.id)}
                      onDelete={() => handleDelete(computer.id)}
                    />
                  </ServerCard>
                </ServerCardViewport>
              );
            })
          )}
        </Main>
        </DashboardArea>}
      </Content>

      {expandedComputer && (
        <CardModalBackdrop
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setExpandedComputerId(null);
            }
          }}
        >
          <CardModalDialog
            role="dialog"
            aria-modal="true"
            aria-label={`Detalhes ampliados do servidor ${expandedComputerName}`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <CardModalClose
              type="button"
              autoFocus
              aria-label="Fechar card ampliado"
              title="Fechar"
              onClick={() => setExpandedComputerId(null)}
            >
              <X aria-hidden="true" />
            </CardModalClose>

            <ExpandedServerCard
              aria-label={`Servidor ${expandedComputerName}`}
              $tone={expandedComputerTone}
              $problematic={expandedComputerProblem}
            >
              <ServerCardContent
                expanded
                computer={expandedComputer}
                computerHistory={expandedComputerHistory}
                displayName={expandedComputerName}
                isOnline={expandedComputerOnline}
                isProblem={expandedComputerProblem}
                tone={expandedComputerTone}
                onOpenHistory={() => {
                  setExpandedComputerId(null);
                  handleOpenHistory(expandedComputer);
                }}
                onEdit={() => handleEdit(expandedComputer.id)}
                onDelete={() => handleDelete(expandedComputer.id)}
              />
            </ExpandedServerCard>
          </CardModalDialog>
        </CardModalBackdrop>
      )}

      <HistorySidebar
        computer={selectedComputer}
        show={showHistorySidebar}
        onClose={handleCloseHistory}
      />
    </Container>
  );
};

export default Dashboard;
